import cx from "classnames";
import getConfig from "next/config";
import dynamic from "next/dynamic";
import type { ComponentType } from "react";

const { publicRuntimeConfig } = getConfig();

type RenderSanitizedRichTextProps = {
  value: string;
  iframelyApiKey: string;
};

const RenderSanitizedRichText = dynamic<RenderSanitizedRichTextProps>(
  () =>
    import(
      "@hopin-team/ckeditor5-build-hopin/dist/render-rich-text"
    ) as Promise<{ default: ComponentType<RenderSanitizedRichTextProps> }>,
  { ssr: false }
);

type VideoPublicDescriptionProps = {
  html: string;
  /** ~1–3 lines: natural height only (player stack stays vertically centered). */
  compact?: boolean;
};

const richTextClassName = [
  "video-public-description-content",
  "break-words font-[Inter] text-base font-normal leading-6 text-white",
  "[&_.ck-content]:text-inherit",
  "[&_a]:no-underline",
  "[&_a]:inline-flex [&_a]:items-center [&_a]:rounded-full [&_a]:bg-white/10 [&_a]:px-2 [&_a]:py-0.5 [&_a]:text-white [&_a]:hover:bg-white/15",
  "[&_.ck-content_p]:!mb-2 [&_.ck-content_p]:!text-base [&_.ck-content_p]:!font-normal [&_.ck-content_p]:!leading-6 [&_.ck-content_p]:last:!mb-0",
  "[&_.ck-content_ol]:mb-2 [&_.ck-content_ol]:list-inside [&_.ck-content_ol]:list-decimal [&_.ck-content_ol]:!pl-0 [&_.ck-content_ol]:last:mb-0",
  "[&_.ck-content_ul]:mb-2 [&_.ck-content_ul]:list-inside [&_.ck-content_ul]:list-disc [&_.ck-content_ul]:!pl-0 [&_.ck-content_ul]:last:mb-0",
  "[&_.ck-content_li]:!mb-1 [&_.ck-content_li]:!pl-0 [&_.ck-content_li]:!text-base [&_.ck-content_li]:!font-normal [&_.ck-content_li]:!leading-6 [&_.ck-content_li]:marker:text-white",
  "[&_.ck-content_strong]:font-semibold [&_.ck-content_strong]:text-white",
].join(" ");

const scrollableBodyClassName = [
  richTextClassName,
  "min-h-0 flex-1 overflow-y-auto overscroll-contain",
  "[scrollbar-width:none] [-ms-overflow-style:none]",
  "[&::-webkit-scrollbar]:hidden",
].join(" ");

/** Same surface as InfoBar title — overlay backdrop shows through (no solid black bar). */
const panelShellClassName = "w-full bg-transparent";

/** Tablet: match InfoBar `padding: 16px`. Desktop: InnerContainer already has 24px — no extra inset. */
const panelInsetClassName = "max-lg:px-4 lg:px-0";

/** Description below the video in the fullscreen player overlay only. */
export function VideoPublicDescription({
  html,
  compact = false,
}: VideoPublicDescriptionProps) {
  return (
    <div
      className={cx(
        "w-full",
        compact ? "shrink-0" : "flex min-h-0 flex-1 flex-col"
      )}
      data-testid="video-public-description"
    >
      <div
        className={cx(
          panelShellClassName,
          compact
            ? "flex shrink-0 flex-col py-3"
            : "flex min-h-0 flex-1 flex-col overflow-hidden py-3",
          panelInsetClassName
        )}
      >
        <div
          className={compact ? richTextClassName : cx(scrollableBodyClassName)}
        >
          <RenderSanitizedRichText
            value={html}
            iframelyApiKey={publicRuntimeConfig.IFRAMELY_API_KEY}
          />
        </div>
      </div>
    </div>
  );
}
